<script setup lang="ts">
/**
 * BottomNav — fixed нижняя навигация для mobile (inbox-282 / Task 2
 * mobile-tailwind plan).
 *
 * 4 router-link slots + центральная круглая FAB через order-3 в flex.
 * Скрыта на lg+ (lg:hidden) — для desktop остаётся sidebar в CabinetLayout.
 * Safe-area inset-bottom учтён через pb-safe utility (Task 0).
 *
 * Тап FAB toggle'ит SpeedDial с 4 actions; backdrop закрывает.
 */

import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { HomeIcon, LinkIcon, PlusIcon, FileTextIcon, MenuIcon } from 'lucide-vue-next';
import SpeedDial from './SpeedDial.vue';

const speedDialOpen = ref(false);
const route = useRoute();

// inbox-352 — на link-detail FAB скрыт чтобы не дублировать topbar actions
// (Pause / Edit / Copy URL уже в шапке). Routes можно расширить.
const FAB_HIDDEN_ROUTES = new Set(['link-detail', 'bio-constructor', 'campaign-detail']);
const fabVisible = computed(() => !FAB_HIDDEN_ROUTES.has(String(route.name)));

interface NavItem {
  to: string;
  label: string;
  icon: typeof HomeIcon;
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Главная', icon: HomeIcon },
  { to: '/links', label: 'Ссылки', icon: LinkIcon },
  // FAB вставляется через order-3 между 2-й и 3-й RouterLink в flex
  // inbox-423 — было /analytics-dashboards (Стата). Био чаще тапается
  // на mobile, аналитика идёт через /menu sub-nav.
  { to: '/bio', label: 'Био', icon: FileTextIcon },
  { to: '/menu', label: 'Меню', icon: MenuIcon },
];
</script>

<template>
  <nav
    data-testid="bottom-nav"
    class="fixed bottom-0 inset-x-0 z-40 bg-surface-2 border-t border-border
           flex justify-around items-end pb-safe lg:hidden"
  >
    <RouterLink
      v-for="item in navItems"
      :key="item.to"
      :to="item.to"
      class="flex flex-col items-center gap-1 py-2 px-3 min-w-[64px] min-h-[44px]
             text-fg-muted no-underline"
      active-class="text-accent-console"
    >
      <component :is="item.icon" class="size-6" />
      <span class="text-[11px]">{{ item.label }}</span>
    </RouterLink>

    <button
      v-if="fabVisible"
      type="button"
      aria-label="Создать"
      data-testid="bottom-nav-fab"
      class="order-3 -mt-6 w-14 h-14 shrink-0 rounded-full bg-accent-console text-white shadow-lg
             flex items-center justify-center active:scale-95 transition-transform"
      @click="speedDialOpen = !speedDialOpen"
    >
      <PlusIcon class="size-7" />
    </button>
  </nav>

  <SpeedDial :open="speedDialOpen" @close="speedDialOpen = false" />
</template>
